import { ValidationErrors } from '@angular/forms';
import { WorkspaceDatabaseKeys } from '@database/workspace';
import { AppRoutingKeys, fileBooleanMappings } from '@shared';
import { firstValueFrom, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { ValidationKeys } from '../../models';
import { IntervalService } from '../../services/interval.service';
import { IAsyncValidatorHandler } from '../async-validator-handler.interface';
import { CrossTableParams } from '../models/cross-table-params-type';

export class GapsCheck implements IAsyncValidatorHandler {
	private next?: IAsyncValidatorHandler;

	public constructor(
		private intervalService: IntervalService,
		private isEditMode: boolean
	) {}

	public setNext(nextHandler: IAsyncValidatorHandler) {
		this.next = nextHandler;
	}

	public async handle(crossTableParams: CrossTableParams): Promise<ValidationErrors | null> {
    const qp = crossTableParams.queryParamsMap;
    const tableName = qp.get(AppRoutingKeys.queryParamsMap.tableName);
    const workfileId = qp.get(AppRoutingKeys.queryParamsMap.workfileId);
    const projectNumber = qp.get(AppRoutingKeys.queryParamsMap.projectNumber);
    const entityName = qp.get(AppRoutingKeys.queryParamsMap.entityName);
    const entryId = qp.get(AppRoutingKeys.queryParamsMap.entryId);
    const childEntryId = qp.get(AppRoutingKeys.queryParamsMap.childEntryId);

    // Resolve interval boundaries once
    const fromValue = crossTableParams.crossControlKey === WorkspaceDatabaseKeys.columns.depthFrom
      ? crossTableParams.crossControlValue
      : crossTableParams.controlValue;

    const toValue = crossTableParams.crossControlKey === WorkspaceDatabaseKeys.columns.depthTo
      ? crossTableParams.crossControlValue
      : crossTableParams.controlValue;

    // Make allowGaps explicit with a safe default (false = no gaps allowed)
    const allowGapsKey = crossTableParams.tableMetadata[WorkspaceDatabaseKeys.tables.allowGaps];
    const allowGaps = fileBooleanMappings[allowGapsKey] ?? false;

		const gapCheck: ValidationErrors | null = await firstValueFrom(
      of(allowGaps).pipe(
        switchMap((gapsAllowed: boolean) => {
          if (!gapsAllowed) {
            return this.intervalService
              .isIntervalBreached$(
                tableName,
                workfileId,
                projectNumber,
                entityName,
                entryId,
                childEntryId,
                fromValue,
                toValue
              )
              .pipe(
                switchMap((dataInExistingInterval: boolean) => {
                  if (!dataInExistingInterval) {
                    return this.intervalService
                      .isGapInData$(
                        tableName,
                        workfileId,
                        projectNumber,
                        entityName,
                        entryId,
                        childEntryId,
                        fromValue,
                        toValue
                      )
                      .pipe(
                        switchMap((gapFound: boolean) => {
                          return this.intervalService
                            .isDuplicateInterval$(
                              tableName,
                              workfileId,
                              projectNumber,
                              entityName,
                              entryId,
                              childEntryId,
                              fromValue,
                              toValue
                            )
                            .pipe(
                              switchMap((isDuplicateData: boolean) => {
                                // If a gap is found, that validation wins
                                if (gapFound) {
                                  return of({ [ValidationKeys.validationRules.intervalGapRule]: true });
                                }

                                // Edit-mode corruption prevention with self-duplicate allowance
                                if (this.isEditMode) {
                                  if (isDuplicateData) {
                                    // Duplicate exists; allow only if it belongs to the current record
                                    return this.intervalService
                                      .isDuplicateOwnedByCurrentEntry$(
                                        tableName!,
                                        workfileId!,
                                        projectNumber!,
                                        entityName!,
                                        entryId!,
                                        childEntryId!,
                                        Number(fromValue as any),
                                        Number(toValue as any)
                                      )
                                      .pipe(
                                        map((isSelfDuplicate: boolean) =>
                                          isSelfDuplicate
                                            ? null
                                            : { [ValidationKeys.validationRules.preventIntervalCorruptionRule]: true }
                                        )
                                      );
                                  }
                                  // No duplicate found in edit mode → allow (single-record edit)
                                  return of(null);
                                }

                                // Not edit mode and no gap → no error
                                return of(null);
                              })
                            );
                        })
                      );
                  }
                  return of(null);
                })
              );
          }
          return of(null);
        })
      )
    );

		if (gapCheck) {
			return gapCheck;
		}

		if (this.next) {
			return await this.next.handle(crossTableParams);
		}

		return null;
	}
}
